import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { ChevronRight, Loader2, Music, Clock, Calendar, Edit, FileText, TrendingUp } from "lucide-react";
import SearchBar2 from "../SearchBar2";
import StatCard from "./ui/StatCard";
import MiniChart from "./ui/MiniChart";
import ActivityChart from "./ui/ActivityChart";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../src/components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from"../../../src/components/ui/table";
import CircleChart from "./ui/CircleChart";
import ReportStatusChart from "./ui/ReportStatusChart";

const AdminSongs = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.dir() === "rtl";
  const [songs, setSongs] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [songsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState("Month");
  const [admins, setAdmins] = useState([]);
  const [error, setError] = useState("");
  const [songCount, setSongCount] = useState(0);
  const [songsAddedThisMonth, setSongsAddedThisMonth] = useState(0);
  const [knowledgeGain, setKnowledgeGain] = useState("0%");
  const [lastSync, setLastSync] = useState(null);
  const [editedSongsCount, setEditedSongsCount] = useState(0);
  const [editedReportsCount, setEditedReportsCount] = useState(0);
  const [lastThreeMonths, setLastThreeMonths] = useState([]);

  // Check admin access
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const role = localStorage.getItem("adminRole");
    if (!token || !["admin", "super user"].includes(role)) {
      navigate("/admin");
    } else {
      setUserRole(role);
      if (role === "admin") {
        fetchAdmins();
      }
    }
  }, [navigate]);

  // Fetch all dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("adminToken");
        const [songCountRes, songsAddedRes, lastSyncRes, editedSongsRes, editedReportsRes, songsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongsCount`),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sync/songs-added-this-month`),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sync/last-sync`),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/song-edits/count`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/reports`),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongs?page=${currentPage}&limit=${songsPerPage}`),
        ]);

        if (!songCountRes.ok) throw new Error(`Song count error: ${songCountRes.status}`);
        if (!songsAddedRes.ok) throw new Error(`Songs added error: ${songsAddedRes.status}`);
        if (!lastSyncRes.ok) throw new Error(`Last sync error: ${lastSyncRes.status}`);
        if (!editedSongsRes.ok) throw new Error(`Edited songs error: ${editedSongsRes.status}`);
        if (!editedReportsRes.ok) throw new Error(`Edited reports error: ${editedReportsRes.status}`);
        if (!songsRes.ok) throw new Error(`Songs fetch error: ${songsRes.status}`);

        const [songCountData, songsAddedData, lastSyncData, editedSongsData, editedReportsData, songsData] = await Promise.all([
          songCountRes.json(),
          songsAddedRes.json(),
          lastSyncRes.json(),
          editedSongsRes.json(),
          editedReportsRes.json(),
          songsRes.json(),
        ]);

        setSongCount(songCountData.count || 0);
        setSongsAddedThisMonth(songsAddedData.totalSongsAdded || 0);
        setEditedSongsCount(editedSongsData.count || 0);
        setEditedReportsCount(editedReportsData.reports?.length || 0);

        const syncData = lastSyncData.data || lastSyncData;
        if (Array.isArray(syncData) && syncData.length > 0) {
          const sortedSyncs = [...syncData].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          setLastSync(sortedSyncs[0]);
        } else {
          setLastSync(syncData);
        }

        if (songsData.songs && Array.isArray(songsData.songs)) {
          setSongs(songsData.songs);
          setTotalPages(songsData.pagination?.pages || 1);
        } else {
          setSongs([]);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error(t("admin.errorFetchingData"));
        setSongs([]);
        setTotalPages(1);
        setSongCount(0);
        setSongsAddedThisMonth(0);
        setEditedSongsCount(0);
        setEditedReportsCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [t, currentPage, songsPerPage]);

  // Calculate knowledge gain
  useEffect(() => {
    if (songCount > 0 && songsAddedThisMonth >= 0) {
      const percentage = ((songsAddedThisMonth / songCount) * 100).toFixed(1);
      setKnowledgeGain(`+${percentage}%`);
    } else {
      setKnowledgeGain("+0%");
    }
  }, [songCount, songsAddedThisMonth]);

  // Fetch songs added last year for CircleChart
  useEffect(() => {
    const fetchSongsAddedLastYear = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/sync/songs-added-this-month`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data && Array.isArray(data.data)) {
          // Get last 3 months (handle wrap-around for Jan/Feb)
          const now = new Date();
          const months = data.data;
          const last3 = [];
          for (let i = 2; i >= 0; i--) {
            let monthIdx = (now.getMonth() - i + 12) % 12;
            const monthData = months.find(m => m.month === monthIdx + 1);
            last3.push({
              name: monthData?.name || '',
              value: monthData?.totalSongsAdded || 0,
              month: monthIdx + 1,
            });
          }
          setLastThreeMonths(last3);
        }
      } catch (error) {
        setLastThreeMonths([]);
      }
    };
    fetchSongsAddedLastYear();
  }, [t, currentPage, songsPerPage]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return t("admin.never");
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(i18n.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Calculate next scheduled sync time
  const getNextScheduledSync = () => {
    const now = new Date();
    const nextSync = new Date(now);
    nextSync.setDate(now.getDate() + ((7 - now.getDay()) % 7));
    nextSync.setHours(2, 0, 0, 0);

    if (now.getDay() === 0 && now.getHours() < 2) {
      nextSync.setDate(now.getDate());
    }

    if (nextSync <= now) {
      nextSync.setDate(nextSync.getDate() + 7);
    }

    return formatDate(nextSync);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/logout`);
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminRole");
      navigate("/admin");
      window.location.reload();
    } catch (err) {
      console.error("Logout error:", err);
      toast.error(t("admin.logoutError"));
    }
  };

  const fetchAdmins = async (page = 1) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/users?page=${page}&limit=10`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const mappedAdmins = response.data.map((admin) => ({
        id: admin._id,
        name: admin.username,
        displayEmail: admin.username.toLowerCase() + "@praisesawa.com",
        email: admin.username.toLowerCase() + "@praisesawa.com",
        role: admin.role,
        employment: admin.role === "admin" ? "Full Access" : "Limited Access",
        status: "Active",
        note: admin.role === "admin" ? "System Admin" : "Content Manager",
      }));
      setAdmins(mappedAdmins);
      setTotalPages(Math.ceil(response.data.length / 10));
      setCurrentPage(page);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        setError(
          err.response?.data?.message || t("adminSettings.error.fetchAdmins")
        );
        toast.error(t("adminSettings.error.fetchAdmins"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit song navigation
  const handleEditSong = (song) => {
    if (song?.songID) {
      navigate(`/edit-song/${song.songID}`);
    } else {
      console.error("Invalid song ID:", song);
      toast.error(t("admin.invalidSongID"));
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className={`flex h-full overflow-hidden bg-white ${isRTL ? "rtl" : "ltr"}`}>
      <div className="flex-1 overflow-auto">
        <main className="px-3 sm:px-6 py-4 sm:py-6">
          {/* --- Dashboard Section (copied from MainDashboard) --- */}
          <div className="flex flex-col gap-4 pb-5">
            {/* Top: StatCards grid and ReportStatusChart */}
            <div className="flex flex-col lg:flex-row gap-4" style={{ minHeight: 260 }}>
              {/* StatCards grid */}
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 grid-rows-2 gap-4 flex-1"
              >
                <StatCard
                  title={t("admin.totalSongs")}
                  value={songCount.toString()}
                  subtitle={t("admin.songs")}
                  icon={<Music className="w-7 h-7 text-gray-400" />}
                />
                <StatCard
                  title={t("admin.lastSync")}
                  value={lastSync ? formatDate(lastSync.timestamp) : t("admin.never")}
                  icon={<Clock className="w-7 h-7 text-gray-400" />}
                />
                <StatCard
                  title={t("admin.nextScheduledSync")}
                  value={getNextScheduledSync()}
                  icon={<Calendar className="w-7 h-7 text-gray-400" />}
                />
                <StatCard
                  title={t("admin.editedSongs")}
                  value={editedSongsCount.toString()}
                  subtitle={t("admin.songs")}
                  icon={<Edit className="w-7 h-7 text-gray-400" />}
                />
                <StatCard
                  title={t("admin.editedReports")}
                  value={editedReportsCount.toString()}
                  subtitle={t("admin.reports")}
                  icon={<FileText className="w-7 h-7 text-gray-400" />}
                />
                <StatCard
                  title={t("admin.persangeThisMonth")}
                  value={knowledgeGain}
                  chart={<MiniChart />}
                  icon={<TrendingUp className="w-7 h-7 text-orange-500" />}
                />
              </div>
              {/* ReportStatusChart spans both rows */}
              <div
                className="flex-1 flex flex-col"
                style={{ minWidth: 0, height: "100%" }}
              >
                <ReportStatusChart style={{ height: "100%" }} />
              </div>
            </div>
            {/* Bottom: ActivityChart and CircleChart */}
            <div className="flex flex-col lg:flex-row gap-4">
              <div
                className="flex-1"
              >
                <ActivityChart
                  title={t("admin.activity")}
                  filter={activityFilter}
                  onFilterChange={setActivityFilter}
                />
              </div>
              <div
                className="flex-1 flex items-center justify-center"
                style={{ minWidth: 0 }}
              >
                <CircleChart lastThreeMonths={lastThreeMonths} />
              </div>
            </div>
          </div>
          {/* --- End Dashboard Section --- */}

          <Card className="rounded-[19px] border-[0.32px] border-gray-100 shadow-[0px_0px_1px_0px_rgba(0,0,0,0.04),0px_2px_6px_0px_rgba(0,0,0,0.04),0px_16px_24px_0px_rgba(0,0,0,0.06)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl font-semibold text-neutral-700">
                {t("admin.manageSongs")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <SearchBar2 navigationType="edit" />
              </div>
              {isLoading ? (
                <div className="flex justify-center items-center min-h-[200px]">
                  <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error}</div>
              ) : songs.length > 0 ? (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-normal text-zinc-400 text-sm">
                          {t("admin.songTitle")}
                        </TableHead>
                        <TableHead className="font-normal text-zinc-400 text-sm">
                          {t("admin.songID")}
                        </TableHead>
                        <TableHead className="font-normal text-zinc-400 text-sm">
                          {t("admin.chorus")}
                        </TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {songs.map((song) => (
                        <TableRow
                          key={song.songID || `song-${Math.random()}`}
                          className="border-t border-gray-100"
                        >
                          <TableCell className="py-4">
                            <div className="flex items-center">
                              <ChevronRight className="mr-2 h-4 w-4 text-muted-foreground" />
                              <span className="text-neutral-700 text-sm">
                                {song.title || "Untitled"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-stone-300 text-sm">
                            {song.songID || "N/A"}
                          </TableCell>
                          <TableCell className="text-stone-300 text-sm hidden md:table-cell">
                            {song.chorus && song.chorus.length > 0
                              ? song.chorus[0]
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() => handleEditSong(song)}
                              disabled={!song.songID}
                              className={`w-24 h-6 rounded-lg text-center text-white text-base font-normal tracking-wide outline outline-1 outline-gray-100 transition-colors ${
                                song.songID
                                  ? "bg-[#333] hover:bg-black"
                                  : "bg-gray-400 cursor-not-allowed"
                              }`}
                            >
                              {t("admin.edit")}
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="flex justify-between items-center mt-6">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-full ${
                        currentPage === 1
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-800 hover:bg-gray-100"
                      }`}
                    >
                      <FiChevronLeft size={24} />
                    </button>
                    <span className="text-gray-600">
                      {t("admin.page")} {currentPage} {t("admin.of")} {totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-full ${
                        currentPage === totalPages
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-800 hover:bg-gray-100"
                      }`}
                    >
                      <FiChevronRight size={24} />
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {t("admin.noSongsFound")}
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
};

export default AdminSongs;